import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { CalendarIcon, Upload, Trash2 } from "lucide-react";
import NavigationHeader from "../components/NavigationHeader";

interface PropertyManagementFormData {
  // معلومات المالك
  owner_full_name: string;
  owner_phone: string;
  owner_email: string;
  owner_id_number: string;
  owner_id_image: FileList;
  
  // معلومات العقار
  property_address: string;
  property_type: string;
  property_area: number;
  detailed_address: string;
  property_age: number;
  ownership_document: FileList;
  
  // معلومات إضافية
  previous_rental_date?: string;
  maintenance_date?: string;
  additional_details?: string;
  property_images: FileList;
  
  // تفضيلات الإدارة
  management_type: string;
  expected_rental_value: number;
  preferred_contract_duration: number;
}

const PropertyManagementRequest = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<{[key: string]: string[]}>({});
  
  const form = useForm<PropertyManagementFormData>();

  const uploadFile = async (file: File, bucket: string): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(2)}.${fileExt}`;
    const filePath = `${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from(bucket)
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleMultipleFileUpload = async (files: FileList, fieldName: string) => {
    const urls: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      try {
        const url = await uploadFile(files[i], 'property-images');
        urls.push(url);
      } catch (error) {
        console.error('Error uploading file:', error);
        toast.error(`فشل في رفع الملف: ${files[i].name}`);
      }
    }
    
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), ...urls]
    }));
    
    return urls;
  };

  const onSubmit = async (data: PropertyManagementFormData) => {
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("يجب تسجيل الدخول أولاً");
        return;
      }

      let ownerIdImageUrl = '';
      let ownershipDocumentUrl = '';
      let propertyImagesUrls: string[] = [];

      // رفع صورة البطاقة الشخصية
      if (data.owner_id_image?.[0]) {
        ownerIdImageUrl = await uploadFile(data.owner_id_image[0], 'property-images');
      }

      // رفع مستند الملكية
      if (data.ownership_document?.[0]) {
        ownershipDocumentUrl = await uploadFile(data.ownership_document[0], 'property-images');
      }

      // رفع صور العقار
      if (data.property_images?.length > 0) {
        propertyImagesUrls = await handleMultipleFileUpload(data.property_images, 'property_images');
      }

      // إضافة الصور المرفوعة مسبقاً
      if (uploadedFiles.property_images) {
        propertyImagesUrls = [...propertyImagesUrls, ...uploadedFiles.property_images];
      }

      const { error } = await supabase
        .from('property_management_requests')
        .insert({
          user_id: user.id,
          owner_full_name: data.owner_full_name,
          owner_phone: data.owner_phone,
          owner_email: data.owner_email,
          owner_id_number: data.owner_id_number,
          owner_id_image_url: ownerIdImageUrl,
          property_address: data.property_address,
          property_type: data.property_type,
          property_area: data.property_area,
          detailed_address: data.detailed_address,
          property_age: data.property_age,
          ownership_document_url: ownershipDocumentUrl,
          previous_rental_date: data.previous_rental_date || null,
          maintenance_date: data.maintenance_date || null,
          additional_details: data.additional_details || null,
          property_images_urls: propertyImagesUrls,
          management_type: data.management_type,
          expected_rental_value: data.expected_rental_value,
          preferred_contract_duration: data.preferred_contract_duration,
        });

      if (error) throw error;

      toast.success("تم إرسال طلب إدارة العقار بنجاح!");
      form.reset();
      setUploadedFiles({});
      
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error("حدث خطأ في إرسال الطلب");
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeUploadedFile = (fieldName: string, index: number) => {
    setUploadedFiles(prev => ({
      ...prev,
      [fieldName]: prev[fieldName]?.filter((_, i) => i !== index) || []
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted">
      <NavigationHeader />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-bold text-primary">
                طلب إدارة العقار
              </CardTitle>
              <p className="text-muted-foreground mt-2">
                املأ النموذج التالي لطلب إدارة عقارك من قبل فريقنا المختص
              </p>
            </CardHeader>
            
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                  
                  {/* معلومات المالك */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-primary border-b pb-2">معلومات المالك</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="owner_full_name"
                        rules={{ required: "الاسم الكامل مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>الاسم الكامل *</FormLabel>
                            <FormControl>
                              <Input placeholder="أدخل الاسم الكامل" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="owner_phone"
                        rules={{ required: "رقم الهاتف مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>رقم الهاتف *</FormLabel>
                            <FormControl>
                              <Input type="tel" placeholder="أدخل رقم الهاتف" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="owner_email"
                        rules={{ required: "البريد الإلكتروني مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>البريد الإلكتروني *</FormLabel>
                            <FormControl>
                              <Input type="email" placeholder="أدخل البريد الإلكتروني" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="owner_id_number"
                        rules={{ required: "رقم البطاقة الشخصية مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>رقم البطاقة الشخصية *</FormLabel>
                            <FormControl>
                              <Input placeholder="أدخل رقم البطاقة الشخصية" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="owner_id_image"
                      rules={{ required: "صورة البطاقة الشخصية مطلوبة" }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <FormItem>
                          <FormLabel>صورة البطاقة الشخصية *</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-4">
                              <Input
                                type="file"
                                accept="image/*"
                                onChange={(e) => onChange(e.target.files)}
                                {...field}
                              />
                              <Upload className="h-5 w-5 text-muted-foreground" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* معلومات العقار */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-primary border-b pb-2">معلومات العقار</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="property_address"
                        rules={{ required: "عنوان العقار مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>عنوان العقار *</FormLabel>
                            <FormControl>
                              <Input placeholder="أدخل عنوان العقار" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="property_type"
                        rules={{ required: "نوع العقار مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>نوع العقار *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="اختر نوع العقار" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="apartment">شقة</SelectItem>
                                <SelectItem value="villa">فيلا</SelectItem>
                                <SelectItem value="office">مكتب</SelectItem>
                                <SelectItem value="shop">محل تجاري</SelectItem>
                                <SelectItem value="warehouse">مستودع</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="property_area"
                        rules={{ required: "المساحة مطلوبة" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>المساحة (متر مربع) *</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="أدخل المساحة" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="property_age"
                        rules={{ required: "عمر العقار مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>عمر العقار (سنوات) *</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="أدخل عمر العقار" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="detailed_address"
                      rules={{ required: "العنوان التفصيلي مطلوب" }}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>العنوان التفصيلي *</FormLabel>
                          <FormControl>
                            <Textarea placeholder="أدخل العنوان التفصيلي للعقار" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="ownership_document"
                      rules={{ required: "مستند الملكية مطلوب" }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <FormItem>
                          <FormLabel>مستند الملكية *</FormLabel>
                          <FormControl>
                            <div className="flex items-center gap-4">
                              <Input
                                type="file"
                                accept="image/*,.pdf"
                                onChange={(e) => onChange(e.target.files)}
                                {...field}
                              />
                              <Upload className="h-5 w-5 text-muted-foreground" />
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* معلومات إضافية */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-primary border-b pb-2">معلومات إضافية</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="previous_rental_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>تاريخ التأجير السابق</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="maintenance_date"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>تاريخ الصيانة</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="additional_details"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>تفاصيل إضافية</FormLabel>
                          <FormControl>
                            <Textarea placeholder="أدخل أي تفاصيل إضافية تريد إضافتها" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="property_images"
                      rules={{ required: "صور العقار مطلوبة" }}
                      render={({ field: { onChange, value, ...field } }) => (
                        <FormItem>
                          <FormLabel>صور العقار *</FormLabel>
                          <FormControl>
                            <div className="space-y-4">
                              <div className="flex items-center gap-4">
                                <Input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={(e) => onChange(e.target.files)}
                                  {...field}
                                />
                                <Upload className="h-5 w-5 text-muted-foreground" />
                              </div>
                              
                              {uploadedFiles.property_images && uploadedFiles.property_images.length > 0 && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                  {uploadedFiles.property_images.map((url, index) => (
                                    <div key={index} className="relative">
                                      <img src={url} alt={`صورة العقار ${index + 1}`} className="w-full h-24 object-cover rounded" />
                                      <Button
                                        type="button"
                                        variant="destructive"
                                        size="sm"
                                        className="absolute top-1 right-1"
                                        onClick={() => removeUploadedFile('property_images', index)}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* تفضيلات الإدارة */}
                  <div className="space-y-6">
                    <h3 className="text-xl font-semibold text-primary border-b pb-2">تفضيلات الإدارة</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={form.control}
                        name="management_type"
                        rules={{ required: "نوع الإدارة مطلوب" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>نوع الإدارة المطلوبة *</FormLabel>
                            <Select onValueChange={field.onChange} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="اختر نوع الإدارة" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="full">إدارة كاملة</SelectItem>
                                <SelectItem value="partial">إدارة جزئية</SelectItem>
                                <SelectItem value="marketing">تسويق فقط</SelectItem>
                                <SelectItem value="maintenance">صيانة فقط</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="expected_rental_value"
                        rules={{ required: "القيمة الإيجارية مطلوبة" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>القيمة الإيجارية المتوقعة *</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="أدخل القيمة بالريال" {...field} onChange={(e) => field.onChange(parseFloat(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="preferred_contract_duration"
                        rules={{ required: "مدة العقد مطلوبة" }}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>مدة العقد المفضلة (بالأشهر) *</FormLabel>
                            <FormControl>
                              <Input type="number" placeholder="أدخل المدة بالأشهر" {...field} onChange={(e) => field.onChange(parseInt(e.target.value))} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  <div className="flex justify-center pt-6">
                    <Button type="submit" disabled={isSubmitting} className="px-8 py-3 text-lg">
                      {isSubmitting ? "جاري الإرسال..." : "إرسال الطلب"}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PropertyManagementRequest;